import { configure } from "mobx";
import { createContext } from "react";
import type { Account } from "../types/account.model";
import type { ApplicationModel } from "../types/application.model";
import type { Member } from "../types/member";
import type { Solution } from "../types/solution";
import type { UserPreferences } from "../types/user-preferences";
import { ApplicationStore } from "./application.store";
import type { ImportType } from "./config.store";
import { ConfigStore } from "./config.store";
import { CredentialStore } from "./credential.store";
import { IntegrationStore } from "./integration.store";
import { MembersStore } from "./member.store";
import { ModalStore } from "./modal.store";
import { TeamsStore } from "./teams";
import { ThemeStore } from "./theme.store";

configure({ isolateGlobalState: true });

export class RootStore {
  config: ConfigStore;
  integrations: IntegrationStore;
  credentials: CredentialStore;
  applications: ApplicationStore;
  theme: ThemeStore;
  modal: ModalStore;
  members: MembersStore;
  teams: TeamsStore;

  constructor() {
    this.config = new ConfigStore();
    this.integrations = new IntegrationStore();
    this.credentials = new CredentialStore();
    this.applications = new ApplicationStore();
    this.theme = new ThemeStore();
    this.modal = new ModalStore();
    this.members = new MembersStore();
    this.teams = new TeamsStore();
  }

  init(
    accountId: string,
    role: string,
    applicationId: string | undefined,
    accessToken: string | undefined,
    smartsuiteBackendUrl: string,
    applications: ApplicationModel[] | undefined,
    userPreferences: UserPreferences,
    solution: Solution,
    member: Member,
    account: Account,
    recordsLimit: number,
    importType: ImportType,
    featureFlags: Record<string, boolean>,
  ): void {
    this.config.init(
      accountId,
      role,
      solution.id,
      accessToken,
      smartsuiteBackendUrl,
      userPreferences,
      solution,
      member,
      account,
      recordsLimit,
      featureFlags,
    );

    // checking due to how monolith works
    if (applications !== undefined && applications.length > 0) {
      this.applications.setList(applications);
      this.applications.setSelectedApplication(
        applications.find((app) => app.id === applicationId),
      );
    }

    this.config.setImportType(importType);
  }

  reset(): void {
    this.config = new ConfigStore();
    this.integrations = new IntegrationStore();
    this.credentials = new CredentialStore();
    this.applications = new ApplicationStore();
    this.theme = new ThemeStore();
    this.modal = new ModalStore();
    this.members = new MembersStore();
    this.teams = new TeamsStore();
  }
}

export const rootStore = new RootStore();
export const RootStoreContext = createContext(rootStore);
// export const useTheme = (): ThemeStore => useContext(ThemeContext);
